import { z } from 'zod';
// =========================================================
// Schemas Zod de los endpoints de lectura de coladas.
// =========================================================
const ESTADO_VALIDO = ['sugerida', 'confirmada', 'digitada', 'cerrada', 'anulada'];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Query params del listado `GET /api/coladas`.
 *
 *  - `estado` aceptá repetición tipo `?estado=sugerida&estado=confirmada`.
 *    En el handler convertimos `request.query.getAll('estado')` a array,
 *    si el handler entrega un string lo envolvemos en array de 1.
 *  - `id_planta`, `pagina`, `por_pagina` se coercen desde string.
 *  - `por_pagina` máx 200 (default 50). `pagina` mín 1 (default 1).
 *  - `desde`/`hasta` opcionales, formato YYYY-MM-DD.
 *  - `q` substring case-insensitive sobre `destino_raw` y nombre canónico.
 */
/**
 * Filtro por N° Pedido Ensamblado BC (array, mismo patrón que `estado`):
 *  - `[]`/ausente → no filtra ("todos").
 *  - `['con']`   → solo coladas con número de pedido BC asignado (digitadas + cerradas).
 *  - `['sin']`   → solo coladas sin número de pedido BC (sugeridas, confirmadas, anuladas que no llegaron a digitar).
 *  - `['con','sin']` → equivalente a "todos" (cubre el 100% de las filas).
 */
export const PEDIDO_BC_ESTADO_FILTRO = ['con', 'sin'];
/**
 * Ordenamiento del listado.
 *  - 'fecha_desc' (default): por fecha_inicio DESC, codigo_interno DESC.
 *  - 'pedido_bc_asc' / 'pedido_bc_desc': por numero_pedido_ensamblado_bc.
 *    NULLs van al final (cuando hay ascending) o al principio (descending).
 */
export const ORDEN_COLADAS = ['fecha_desc', 'pedido_bc_asc', 'pedido_bc_desc'];
export const ListarColadasRequestSchema = z.object({
    estado: z.array(z.enum(ESTADO_VALIDO)).optional(),
    /** Repetible igual que `estado` (`?id_planta=1&id_planta=2`). */
    id_planta: z.array(z.coerce.number().int().positive()).optional(),
    desde: z.string().regex(ISO_DATE).optional(),
    hasta: z.string().regex(ISO_DATE).optional(),
    q: z.string().min(1).max(200).optional(),
    /**
     * Substring case-insensitive sobre `recetas_blend.nombre_texto`.
     * Útil porque hay variantes como "Muros" / "Muros *" / "210 losa".
     */
    receta_blend_q: z.string().min(1).max(100).optional(),
    /**
     * Filtro por presencia de número de Pedido BC. Array de `'con'`/`'sin'`
     * (repetible igual que `estado`) — vacío o con ambos valores = sin
     * filtro (equivalente al 'todos' de antes, ya que con+sin cubre el 100%
     * de las filas).
     */
    pedido_bc_estado: z.array(z.enum(PEDIDO_BC_ESTADO_FILTRO)).optional(),
    /** Búsqueda parcial sobre numero_pedido_ensamblado_bc. */
    pedido_bc_q: z.string().min(1).max(50).optional(),
    orden: z.enum(ORDEN_COLADAS).optional(),
    pagina: z.coerce.number().int().min(1).default(1),
    por_pagina: z.coerce.number().int().min(1).max(200).default(50),
});
// =========================================================
// Schemas de mutaciones (transiciones de estado).
//
// Los endpoints sin body (confirmar, desconfirmar, desmarcar-digitada,
// cerrar) NO requieren validación de payload. `marcar-digitada` exige
// `numero_pedido_ensamblado_bc` no vacío. `anular` exige
// `motivo_anulacion` no vacío.
// =========================================================
export const MarcarDigitadaRequestSchema = z.object({
    numero_pedido_ensamblado_bc: z.string().trim().min(1).max(50),
    /**
     * Obra opcional. Se asigna junto con el número de pedido. Puede
     * cambiarse o agregarse después con POST /api/coladas/:id/asignar-obra.
     */
    obra_works_no: z.string().trim().min(1).max(20).optional(),
});
export const AnularColadaRequestSchema = z.object({
    motivo_anulacion: z.string().trim().min(1).max(2000),
});
/**
 * Anular varias coladas a la vez con el mismo motivo (ej. lote de
 * coladas cargadas con fecha incorrecta por un reloj de planta
 * desconfigurado). `eliminar_batches`: además de anular, borra los
 * batches de dosificador detrás de esas coladas — necesario para que
 * una recarga posterior del CSV corregido no los salte por dedup
 * (planta + N° de batch), ya que anular solo cambia el estado y no
 * toca `hor.batches`.
 */
export const AnularColadasBulkRequestSchema = z.object({
    ids: z.array(z.coerce.number().int().positive()).min(1).max(100),
    motivo_anulacion: z.string().trim().min(1).max(2000),
    eliminar_batches: z.boolean().optional().default(false),
});
export const AnularColadasBulkResponseSchema = z.object({
    cantidad: z.number().int(),
    ids_anuladas: z.array(z.number().int()),
});
// =========================================================
// Schemas de gestión de batches dentro de una colada (sesión 6).
//
// Modelo: "huérfano" significa colada_batches.excluido = 1.
// El batch sigue vinculado a su colada original (para histórico) pero
// no suma agregados y puede moverse a otra colada `sugerida` por
// decisión manual del operador (sin pasar por la regla de agrupación).
// =========================================================
export const ExcluirBatchRequestSchema = z.object({
    motivo: z.string().trim().min(1).max(500),
});
// =========================================================
// Consolidar (merge) coladas en una principal.
//
// `ids_a_consolidar` debe tener al menos 2 ids únicos. La principal
// será la de id_colada MÁS BAJO (estable, predecible). Las demás
// quedan vacías y se anulan automáticamente con motivo.
// =========================================================
export const ConsolidarColadasRequestSchema = z.object({
    ids_a_consolidar: z.array(z.number().int().positive()).min(2).max(20),
});
//# sourceMappingURL=coladas.js.map